import React, {useState, useEffect, useContext} from 'react';
import { 
  Container, 
  Typography, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  Button, 
  Box, 
  TextField
} from '@mui/material';
import {useNavigate} from "react-router-dom";
import {EmailSubscriptionService} from "../services/EmailSubscriptionService";
import {ConfigContext} from "../ConfigProvider";
import {Helmet} from "react-helmet";

function Unsubscribe() {
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);
  const [keepNews, setKeepNews] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [apiCallResponseMessage, setApiCallResponseMessage] = useState(null);

  const config = useContext(ConfigContext);

  // Regex for basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const navigate = useNavigate();
  // Read the emailAddress query parameter on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('emailAddress');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const handleUnsubscribeAllChange = (event) => {
    const checked = event.target.checked;
    setUnsubscribeAll(checked);
    // Reset "keep news" if unsubscribing from all
    if (checked) {
      setKeepNews(false);
    }
  };

  const handleKeepNewsChange = (event) => {
    setKeepNews(event.target.checked);
  };


  // Handler for email change with validation
  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    if (newEmail && !emailRegex.test(newEmail)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  };

  const [countdown, setCountdown] = useState(null); // null means don't show yet

  const handleSubmit = async (event) => {
    event.preventDefault();

    const preferences = {
      email,
      unsubscribeAll,
      keepNews: !unsubscribeAll && keepNews,
    };

    let unsubscribeMarketingResponse = await EmailSubscriptionService.unsubscribe(preferences.email, config.promoMailDomain);
    let res = unsubscribeMarketingResponse.status === 200;
    if (!preferences.keepNews) {
      res &= (await EmailSubscriptionService.unsubscribe(preferences.email, config.newsMailDomain)).status === 200;
    }

    setApiCallResponseMessage(
        res
            ? "You've been unsubscribed successfully."
            : "An error occurred. Please try again later."
    );

    if (res) {
      setCountdown(5); // Start countdown at 5 seconds
    }
  };

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      navigate('/');
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown, navigate]);


  return (
      <Box className="content" sx={{         
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 2,
        marginBottom: '50px', // 50px page margin at the bottom
        minHeight: 'calc(100vh - 229px)' 
        }}>
        <Helmet>
          <title>PairCare - Unsubscribe from Emails</title>
        </Helmet>
        {apiCallResponseMessage && <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Typography variant="h5" align="center" gutterBottom>
            {apiCallResponseMessage}
          </Typography>
          {countdown !== null && countdown > 0 && (
              <Typography variant="caption" align="center" display="block" gutterBottom>
                Redirecting in {countdown} second{countdown > 1 ? 's' : ''}...
              </Typography>
          )}
        </Container> }
        {!apiCallResponseMessage && <Container maxWidth="sm" sx={{ mt: 4 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Unsubscribe Preferences
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Your Email Address"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              margin="normal"
              error={!!emailError}
              helperText={emailError}
              required
            />
            <FormControlLabel
                control={
                  <Checkbox
                    checked={keepNews}
                    onChange={handleKeepNewsChange}
                    color="primary"
                    disabled={unsubscribeAll}
                  />
                }
                label="Still get weekly/monthly news"
              />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={unsubscribeAll}
                    onChange={handleUnsubscribeAllChange}
                    color="primary"
                  />
                }
                label="Unsubscribe from all"
              />
            </FormGroup>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{ mt: 2 }}
              disabled={ !email || (!unsubscribeAll && !keepNews)}  // Save button enabled only when an email is provided
            >
              Save Preferences
            </Button>

            <Typography marginTop={"20px"} variant="caption" component="p">* Please allow up to 10 business days for your opt-out request to take effect</Typography>
          </Box>
        </Container>}
      </Box>
  );
}

export default Unsubscribe;
